'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const supabase = createClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 dark:from-[#0f172a] dark:via-[#0f172a] dark:to-[#0f172a] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center gap-2.5 group">
            <AppLogo
              src="/assets/images/Logo_big-1771795433731.png"
              size={48}
              text="DogFob Connect"
              className="group-hover:scale-105 transition-transform duration-200"
            />
          </Link>
          <p className="mt-3 text-slate-600 dark:text-slate-400">Reset your password</p>
        </div>

        <div className="feature-card p-8">
          {submitted ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="EnvelopeIcon" size={32} className="text-teal-600 dark:text-teal-400" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">Check your email</h2>
              <p className="text-slate-600 dark:text-slate-400 mb-2">
                We&apos;ve sent a password reset link to:
              </p>
              <p className="font-semibold text-slate-800 dark:text-slate-200 mb-6">{email}</p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mb-8">
                Click the link in the email to reset your password. If you don&apos;t see it, check your spam folder.
              </p>
              <Link
                href="/login"
                className="btn-primary inline-flex items-center gap-2 px-6 py-2.5"
              >
                <Icon name="ArrowLeftIcon" size={18} />
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-1">Forgot your password?</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Enter your email address and we&apos;ll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl flex items-start gap-2">
                    <Icon name="ExclamationCircleIcon" size={20} className="text-red-500 mt-0.5 shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input w-full"
                    placeholder="you@example.com"
                    required
                    autoFocus
                  />
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full py-3">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="EnvelopeIcon" size={20} />
                      Send Reset Link
                    </span>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  href="/login"
                  className="text-sm text-teal-600 dark:text-teal-400 font-semibold hover:text-teal-700 inline-flex items-center gap-1"
                >
                  <Icon name="ArrowLeftIcon" size={16} />
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
